import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { peekIntendedRoute } from '../utils/postAuthRedirect';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Briefcase, User, Building2, Gift, CheckCircle2, Clock, Mail, ArrowRight } from 'lucide-react';
import BackendHealthBanner from '../components/BackendHealthBanner';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [role, setRole] = useState('talent');
  const [referralCode, setReferralCode] = useState('');
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    // Client fields
    company_name: '',
    contact_name: '',
    billing_address: '',
    // Talent fields
    first_name: '',
    last_name: '',
    city: '',
    state: '',
  });
  const [loading, setLoading] = useState(false);
  // Iter-213 P0 — match Login.js: don't let users submit while backend is down.
  const [backendReady, setBackendReady] = useState(true);
  // Iter-213 P0 — Submission confirmation. Client + Agency accounts need
  // approval before they can sign in; instead of an instant redirect
  // (which made it feel like nothing happened), we render a clear
  // "Request submitted — under review" screen with their reference id,
  // expected timeline, and a CTA into the gated pending page.
  const [submittedInfo, setSubmittedInfo] = useState(null); // { email, role, reference_id }
  // Iter-263 — GPS attendance consent. Required for talent registrations.
  // We record the acceptance immediately after the user account is created
  // (POST /api/gps/consent/accept with consent_type='registration').
  const [gpsConsentAccepted, setGpsConsentAccepted] = useState(false);
  const [showConsentDetail, setShowConsentDetail] = useState(false);
  // Iter-299e — SMS opt-in (Twilio A2P CTA). Default unchecked,
  // separate from ToS. Recorded in `sms_consent_log` post-signup.
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);

  // Iter-315p — Marketing attribution bridge.
  // The marketing site (www.ievntur.com) attaches UTM params to every
  // outbound "Log In" / "Start Free" link. We:
  //   1) capture on first landing (persist to localStorage with 30d TTL),
  //   2) rehydrate on refresh / cross-tab navigation,
  //   3) forward on the /api/auth/register payload so it lands in
  //      the signup_attribution table.
  const ATTR_STORE = 'iev:attribution';
  const ATTR_TTL_DAYS = 30;

  const readStoredAttribution = () => {
    try {
      const raw = localStorage.getItem(ATTR_STORE);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.captured_at) return null;
      const ageDays = (Date.now() - new Date(parsed.captured_at).getTime()) / 86400000;
      if (ageDays > ATTR_TTL_DAYS) return null;
      return parsed;
    } catch { return null; }
  };

  // Capture referral code + UTM attribution from the URL. Both persist
  // through refresh / navigation so a bounce to /login and back doesn't
  // wipe them.
  useEffect(() => {
    // Referral code
    const ref = searchParams.get('ref') || sessionStorage.getItem('pending_referral_code');
    if (ref) {
      const code = ref.trim().toUpperCase();
      setReferralCode(code);
      try { sessionStorage.setItem('pending_referral_code', code); } catch { /* ignore */ }
      validateReferralCode(code);
    }

    // Marketing attribution
    const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_page'];
    const captured = {};
    UTM_KEYS.forEach((k) => {
      const v = searchParams.get(k);
      if (v) captured[k] = v;
    });
    if (Object.keys(captured).length > 0) {
      const payload = {
        ...captured,
        landing_url: window.location.pathname + window.location.search,
        referrer: document.referrer || null,
        captured_at: new Date().toISOString(),
      };
      try { localStorage.setItem(ATTR_STORE, JSON.stringify(payload)); } catch { /* ignore quota */ }
    }
  }, [searchParams]);

  // Iter-315p — If the marketing site pre-selected a role (?role=talent
  // or ?role=client), honor it so the correct signup form is prefilled.
  useEffect(() => {
    const r = searchParams.get('role');
    if (r === 'talent' || r === 'client' || r === 'agency') {
      setRole(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Iter-220e — hydrate the form from any saved draft for the current
  // role. Drafts expire after 7 days. Switching role discards the draft
  // for the previous role (it's keyed per-role).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`register-draft:${role}`);
      if (!raw) return;
      const wrapper = JSON.parse(raw);
      if (!wrapper || typeof wrapper !== 'object' || !wrapper.data) return;
      const age = Date.now() - (wrapper.ts || 0);
      const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
      if (age > SEVEN_DAYS) {
        localStorage.removeItem(`register-draft:${role}`);
        return;
      }
      // Merge — never overwrite a value the user just typed (rare race
      // when the user starts typing before this effect runs).
      setFormData((prev) => ({
        ...wrapper.data,
        password: prev.password || '',  // never persist passwords
      }));
    } catch { /* ignore parse errors */ }
  }, [role]);

  const validateReferralCode = async (code) => {
    try {
      const res = await api.get(`/referrals/validate/${code}`);
      if (res.data.valid) {
        setReferrerInfo(res.data);
      }
    } catch {}
  };

  // Iter-257 — Wrong-workspace heuristic. The #1 support ticket is users
  // who pick "Agency" or "Client" when they meant "Talent". We surface a
  // soft inline suggestion before submit instead of letting it through.
  // Heuristic is intentionally conservative — only fires on strong signals:
  //   • Agency / Client picked
  //   • Personal email domain (gmail / yahoo / hotmail / outlook / icloud / aol)
  //   • Company name empty OR company name == first/last name pattern
  // → suggest Talent.
  const wrongWorkspaceSuggestion = (() => {
    if (role === 'talent') return null;
    const email = (formData.email || '').toLowerCase();
    const personal = /(@gmail\.com|@yahoo\.com|@hotmail\.com|@outlook\.com|@icloud\.com|@aol\.com|@me\.com|@live\.com)$/.test(email);
    if (!personal) return null;
    const company = (formData.company_name || '').trim();
    const contact = (formData.contact_name || '').trim().toLowerCase();
    const companyLooksLikeName = company && contact && (
      company.toLowerCase() === contact
      || company.toLowerCase().split(' ').every((w) => contact.includes(w))
    );
    const companyMissing = !company;
    if (companyMissing || companyLooksLikeName) {
      return {
        suggestedRole: 'talent',
        message: 'It looks like you may be trying to sign up as Talent. Personal email + no business name suggests you meant the Talent (Apply for Gigs) account.',
      };
    }
    return null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Iter-220e — trim every user-typed field BEFORE building the
      // payload so whitespace-only inputs (which bypass HTML required)
      // are rejected client-side with a clear toast instead of falling
      // through to a confusing backend 400.
      const trimmed = Object.fromEntries(
        Object.entries(formData).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
      );

      const payload = {
        email: trimmed.email,
        phone: trimmed.phone,
        password: formData.password,           // never trim password
        role,
        referral_code: referralCode || undefined,
      };

      // Iter-315p — Attach any captured marketing attribution so the
      // backend can persist it into signup_attribution. `undefined` if
      // there is nothing to send — pydantic ignores it.
      const attribution = readStoredAttribution();
      if (attribution) {
        payload.attribution = attribution;
      }

      if (role === 'client' || role === 'agency') {
        payload.company_name = trimmed.company_name;
        payload.contact_name = trimmed.contact_name;
        payload.billing_address = trimmed.billing_address;
      } else {
        payload.first_name = trimmed.first_name;
        payload.last_name = trimmed.last_name;
        payload.city = trimmed.city;
        payload.state = trimmed.state;
      }

      // Iter-220e — client-side guard. Surface the missing field BEFORE
      // we ever call the backend so the user sees an actionable message
      // (the legacy backend response was "Name, city, and state required
      // for talent" — fine, but the new pre-validated backend now
      // returns "Missing required fields for talent: last_name" too).
      const requiredForRole = role === 'talent'
        ? ['first_name', 'last_name', 'city', 'state']
        : ['company_name', 'contact_name'];
      const missing = requiredForRole.filter((k) => !payload[k]);
      if (missing.length) {
        toast.error(`Please fill in: ${missing.join(', ').replace(/_/g, ' ')}`);
        setLoading(false);
        return;
      }

      // Iter-263 — Talent must accept GPS Attendance Verification consent
      // before account creation. Clients and agencies are exempt.
      if (role === 'talent' && !gpsConsentAccepted) {
        toast.error('Please accept the GPS Attendance Verification consent to continue.');
        setLoading(false);
        return;
      }

      // Iter-220e — persist a draft of the registration payload so a
      // mid-typing crash / refresh / network failure doesn't wipe the
      // user's input. NEVER persist the password (security). Drafts
      // auto-expire after 7 days and are cleared on successful
      // registration below.
      try {
        // eslint-disable-next-line no-unused-vars
        const { password: _pw, ...draftSafe } = trimmed;
        localStorage.setItem(
          `register-draft:${role}`,
          JSON.stringify({ ts: Date.now(), data: draftSafe }),
        );
      } catch { /* quota / private mode — ignore */ }

      const user = await register(payload);

      // Iter-263 — Record GPS consent for new talent registrations.
      // Best-effort: a failure here should NOT block the user from continuing
      // (we'll re-prompt via the login back-fill modal if it didn't land).
      if (role === 'talent' && gpsConsentAccepted) {
        try {
          await api.post('/gps/consent/accept', {
            consent_type: 'registration',
            // policy_version omitted -> backend stamps current GPS_POLICY_VERSION
          });
        } catch (_) { /* will be re-prompted at next login */ }
      }

      // Iter-299e — Record SMS opt-in for Twilio A2P CTA compliance.
      // Best-effort: failure here doesn't block account creation, but
      // means we won't send SMS to this user until they re-opt-in via
      // Notification Preferences.
      if (smsConsentAccepted && formData.phone) {
        try {
          await api.post('/sms/consent', {
            phone_number: formData.phone,
            consent_version: 'v1',
            consent_method: 'signup_checkbox',
          });
        } catch (_) { /* user can re-opt-in from Notification Prefs */ }
      }

      // Clear ALL role drafts on success so a stale draft from a prior
      // attempt doesn't haunt a future signup.
      try {
        ['talent', 'client', 'agency'].forEach((r) => {
          localStorage.removeItem(`register-draft:${r}`);
        });
        sessionStorage.removeItem('pending_referral_code');
      } catch { /* ignore */ }

      // Iter-213 P0 — For client + agency accounts, show a clear success/under-review
      // screen instead of bouncing them to the pending dashboard immediately.
      // Iter-225q — Agency accounts are now also gated behind admin approval
      // (no more "Your account is ready" bypass). Both client and agency
      // signups land in the pending queue and must wait for approval.
      const isPending =
        (user.role === 'client' || user.role === 'agency') &&
        user.client_approval_status &&
        user.client_approval_status !== 'approved';
      const needsConfirmation = isPending;

      if (needsConfirmation) {
        toast.success('Your account request has been received — pending approval.');
        setSubmittedInfo({
          email: user.email,
          role: user.role,
          under_review: true,
          // Reference id falls back to the user id if a dedicated request id
          // isn't returned by the API yet; either is enough for support to look up.
          reference_id: (user.client_request_id || user.id || '').toString().slice(0, 8).toUpperCase(),
        });
        return; // do NOT navigate — render the success view below
      }

      toast.success('Registration successful!');

      // Preserve a tapped deep link (e.g. a job) through sign-up. We PEEK
      // (not consume) so that if the destination is still behind an IDV /
      // pending gate, ProtectedRoute keeps it stored and returns the user
      // there once the gate is cleared. peekIntendedRoute only yields safe
      // internal paths (no open-redirect risk).
      const deepLink = peekIntendedRoute();
      // Redirect to Home page based on role
      if (user.role === 'client') {
        // Iter-209 — new clients land on pending screen until approved.
        const status = user.client_approval_status;
        if (status && status !== 'approved') {
          navigate('/client/pending');
        } else {
          navigate(deepLink || '/client/home');
        }
      } else if (user.role === 'talent') {
        navigate(deepLink || '/talent/home');
      } else if (user.role === 'agency') {
        navigate('/agency/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    submittedInfo ? (
      // Iter-213 P0 — Submission Confirmation view (client/agency only).
      <div
        className="min-h-screen flex items-center justify-center auth-background py-8 px-4"
        data-testid="register-submitted-page"
      >
        <Card
          className="w-full max-w-xl glassmorphism border-zinc-800"
          data-testid="register-submitted-card"
        >
          <CardHeader className="text-center space-y-3">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-emerald-400" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-white">
              Your account request has been received
            </CardTitle>
            <CardDescription className="text-zinc-300">
              Thanks for signing up with <span className="text-cyan-400 font-medium">iEvntür</span>.
              Your <strong className="text-white">{submittedInfo.role === 'agency' ? 'agency workspace' : 'full-service client'}</strong> account
              is now <strong className="text-white">pending approval</strong>. Our team will review your business and email you once you're cleared to sign in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Reference + account email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-4">
                <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Account email</p>
                <p className="font-mono text-white break-all" data-testid="submitted-email">
                  {submittedInfo.email}
                </p>
              </div>
              <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-4">
                <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Reference ID</p>
                <p className="font-mono text-white" data-testid="submitted-reference-id">
                  {submittedInfo.reference_id || '—'}
                </p>
              </div>
            </div>

            {/* Review timeline */}
            <div
              className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4"
              data-testid="submitted-timeline"
            >
              <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyan-400" />
                What happens next
              </p>
              {/* Iter-225q — both client and agency follow the same review timeline */}
              <ol className="space-y-2 text-sm text-zinc-300">
                <li className="flex gap-3">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[11px] flex items-center justify-center font-semibold">1</span>
                  <span><strong className="text-white">Submitted.</strong> We received your request just now.</span>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-[11px] flex items-center justify-center font-semibold">2</span>
                  <span><strong className="text-white">Under review.</strong> Our team verifies your business — typically within 24 hours.</span>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-zinc-700 border border-zinc-600 text-zinc-300 text-[11px] flex items-center justify-center font-semibold">3</span>
                  <span><strong className="text-white">Approved & active.</strong> You'll get an email and full access {submittedInfo.role === 'agency' ? 'to manage your roster and post jobs' : 'to book talent'}.</span>
                </li>
              </ol>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/client/pending')}
                className="flex-1 h-11 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 font-medium"
                data-testid="submitted-continue-btn"
              >
                Continue to your pending dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => { window.location.href = 'mailto:support@ievntur.com'; }}
                className="h-11 border-zinc-700 text-zinc-200 hover:bg-zinc-800/60"
                data-testid="submitted-contact-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact support
              </Button>
            </div>

            <p className="text-xs text-zinc-500 text-center pt-1">
              Reference ID <span className="font-mono text-zinc-400">{submittedInfo.reference_id || '—'}</span> — quote this if you email us.
            </p>
          </CardContent>
        </Card>
      </div>
    ) : (
    <div className="min-h-screen flex items-center justify-center auth-background py-8" data-testid="register-page">
      <BackendHealthBanner onReady={setBackendReady} />
      <Card className="w-full max-w-md glassmorphism border-zinc-800" data-testid="register-form">
        <CardHeader className="text-center space-y-3">
          <div className="flex justify-center mb-2">
            <img
              src="https://res.cloudinary.com/dzlc6qta0/image/upload/v1771451035/d2e1bd_3ffc4a4bc88e4587b369a584fd8a636b_mv2_pvlple.png"
              alt="iEvntür Logo"
              className="h-12"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Create account</CardTitle>
          <CardDescription className="text-zinc-400" data-testid="register-tagline">
            Launch hybrid campaigns with human + AI talent · Scale beyond physical events.
          </CardDescription>
          {referrerInfo && (
            <div
              className="mt-2 flex flex-col items-center text-center gap-1.5 px-3 py-3 bg-green-900/40 border border-green-700/50 rounded-lg"
              data-testid="referral-banner"
            >
              <div className="flex items-center justify-center gap-2">
                <Gift className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-300">
                  Referred by <strong>{referrerInfo.referrer_name}</strong>
                </span>
              </div>
              <p className="text-xs text-green-200/90 leading-snug" data-testid="referral-benefit-copy">
                {role === 'client' || role === 'agency'
                  ? 'Sign up and unlock your referral reward after your first completed booking.'
                  : 'Sign up and unlock your referral reward after your first completed shift.'}
              </p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Role Selection - Gig Marketplace Language */}
            <div className="space-y-3">
              <Label className="text-white font-medium">I want to</Label>
              <RadioGroup value={role} onValueChange={setRole} data-testid="role-selection">
                {/* Iter-183 P0 — Each card is now a <label htmlFor=…>
                    so the entire box natively acts as a radio target.
                    Replaces the prior <div onClick={…}> wrapper which
                    could miss touch events on Safari iOS and double-fire
                    setRole(). */}
                <label htmlFor="talent" className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  role === 'talent'
                    ? 'border-cyan-500 bg-cyan-500/10'
                    : 'border-zinc-700 hover:border-cyan-500/50'
                }`} data-testid="role-card-talent">
                  <RadioGroupItem value="talent" id="talent" className="border-zinc-500 text-cyan-500" data-testid="role-radio-talent" />
                  <User className={`w-5 h-5 ${role === 'talent' ? 'text-cyan-500' : 'text-zinc-400'}`} />
                  <div className="font-medium text-white flex-1">
                    <span className="block">Talent (Apply for Gigs)</span>
                    <span className="text-sm font-normal text-zinc-400">Browse and work gigs</span>
                  </div>
                </label>
                <label htmlFor="client" className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  role === 'client'
                    ? 'border-cyan-500 bg-cyan-500/10'
                    : 'border-zinc-700 hover:border-cyan-500/50'
                }`} data-testid="role-card-client">
                  <RadioGroupItem value="client" id="client" className="border-zinc-500 text-cyan-500" data-testid="role-radio-client" />
                  <Briefcase className={`w-5 h-5 ${role === 'client' ? 'text-cyan-500' : 'text-zinc-400'}`} />
                  <div className="font-medium text-white flex-1">
                    <span className="block">Hire Talent (Full Service)</span>
                    <span className="text-sm font-normal text-zinc-400">iEvntür-managed staffing with contractor payouts</span>
                  </div>
                </label>
                <label htmlFor="agency" className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  role === 'agency'
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-zinc-700 hover:border-amber-500/50'
                }`} data-testid="role-card-agency">
                  <RadioGroupItem value="agency" id="agency" className="border-zinc-500 text-amber-500" data-testid="role-radio-agency" />
                  <Building2 className={`w-5 h-5 ${role === 'agency' ? 'text-amber-500' : 'text-zinc-400'}`} />
                  <div className="font-medium text-white flex-1">
                    <span className="block">Agency Workspace (Self-Serve)</span>
                    <span className="text-sm font-normal text-zinc-400">Direct booking, you pay talent directly</span>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {wrongWorkspaceSuggestion && (
              <div
                className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3"
                data-testid="wrong-workspace-suggestion"
              >
                <p className="text-sm text-amber-200 leading-snug">
                  {wrongWorkspaceSuggestion.message}
                </p>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setRole(wrongWorkspaceSuggestion.suggestedRole)}
                    className="px-3 py-1.5 rounded-md bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold"
                    data-testid="switch-to-suggested-role-btn"
                  >
                    Switch to Talent
                  </button>
                  <button
                    type="button"
                    onClick={() => {/* keep current role — dismiss inline */}}
                    className="px-3 py-1.5 rounded-md border border-amber-400/40 text-amber-200 text-xs"
                  >
                    Keep my selection
                  </button>
                </div>
              </div>
            )}

            {/* Common Fields */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white">Phone</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="phone-input"
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="password-input"
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>

            {/* Client/Agency-specific fields */}
            {(role === 'client' || role === 'agency') && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-white">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                    data-testid="company-name-input"
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_name" className="text-white">Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    required
                    data-testid="contact-name-input"
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                  />
                </div>
              </>
            )}

            {/* Talent-specific fields */}
            {role === 'talent' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-white">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                      data-testid="first-name-input"
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-white">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                      data-testid="last-name-input"
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-white">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                      data-testid="city-input"
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-white">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      required
                      placeholder="CA"
                      maxLength={2}
                      data-testid="state-input"
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Iter-263 — Talent must accept GPS Attendance Verification consent. */}
            {role === 'talent' && (
              <div
                className="rounded-lg border border-cyan-700/40 bg-cyan-900/10 p-3 space-y-2"
                data-testid="gps-consent-section"
              >
                <div className="flex items-start gap-3">
                  <input
                    id="gps-consent-checkbox"
                    type="checkbox"
                    checked={gpsConsentAccepted}
                    onChange={(e) => setGpsConsentAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-zinc-500 bg-zinc-800 text-cyan-500 focus:ring-cyan-500"
                    data-testid="gps-consent-register-checkbox"
                  />
                  <label htmlFor="gps-consent-checkbox" className="text-sm text-zinc-200 cursor-pointer leading-snug">
                    I consent to GPS attendance verification during my active shifts.
                    Location data is collected <strong>only while I'm clocked in</strong> and is used for
                    attendance verification, shift compliance, fraud prevention, payment validation,
                    and operational safety.{' '}
                    <button
                      type="button"
                      onClick={() => setShowConsentDetail((v) => !v)}
                      className="text-cyan-400 hover:text-cyan-300 underline"
                      data-testid="gps-consent-detail-toggle"
                    >
                      {showConsentDetail ? 'Hide details' : 'Read full policy'}
                    </button>
                  </label>
                </div>
                {showConsentDetail && (
                  <div className="text-xs text-zinc-400 leading-relaxed bg-zinc-900/50 rounded p-2 ml-7 space-y-1.5">
                    <p><strong className="text-zinc-200">When:</strong> Only during active assignments — tracking starts when you successfully clock in and stops automatically at clock out.</p>
                    <p><strong className="text-zinc-200">What you'll see:</strong> A "GPS Tracking Active for Attendance Verification" banner during your shift.</p>
                    <p><strong className="text-zinc-200">If GPS is disabled mid-shift:</strong> Your shift is flagged for admin review and Instant Pay is paused until cleared.</p>
                    <p><strong className="text-zinc-200">Outside work hours:</strong> No tracking. We never collect your location when you're not on a booked shift.</p>
                  </div>
                )}
              </div>
            )}

            {/* Iter-299e — SMS opt-in (Twilio A2P CTA). Unchecked by
                default, separate from ToS, and only visible when the
                user has provided a phone number. */}
            {formData.phone && (
              <div
                className="rounded-lg border border-amber-700/40 bg-amber-900/10 p-3 space-y-2"
                data-testid="sms-consent-section"
              >
                <div className="flex items-start gap-3">
                  <input
                    id="sms-consent-checkbox"
                    type="checkbox"
                    checked={smsConsentAccepted}
                    onChange={(e) => setSmsConsentAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-zinc-500 bg-zinc-800 text-amber-500 focus:ring-amber-500"
                    data-testid="sms-consent-register-checkbox"
                  />
                  <label htmlFor="sms-consent-checkbox" className="text-sm text-zinc-200 cursor-pointer leading-snug">
                    I agree to receive SMS messages from iEvntür regarding
                    booking requests, event opportunities, scheduling updates,
                    account notifications, verification codes, and customer
                    support. Message frequency varies. Message and data rates
                    may apply. Reply <strong>STOP</strong> to opt out and{' '}
                    <strong>HELP</strong> for assistance. See our{' '}
                    <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                      Privacy Policy
                    </a>{' '}and{' '}
                    <a href="/terms-and-conditions" target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                      Terms &amp; Conditions
                    </a>.
                  </label>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 bg-cyan-500 hover:bg-cyan-600 text-white font-medium transition-all"
              disabled={loading || !backendReady}
              data-testid="register-submit-button"
            >
              {loading
                ? 'Creating account...'
                : !backendReady
                ? 'Waiting for server…'
                : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-zinc-400">Already have an account? </span>
            <Link to="/login" className="text-cyan-500 font-medium hover:text-cyan-400 transition-colors" data-testid="login-link">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
    )
  );
};

export default Register;

